/**
 * Google Forms Integration Service
 * Handles gift feedback surveys and automatic response collection
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type GiftFeedback = Database['public']['Tables']['gift_feedback']['Row'];
type GiftSurvey = Database['public']['Tables']['gift_surveys']['Row'];

export interface GoogleFormsConfig {
  service_account_email: string;
  private_key: string;
  form_template_id?: string;
  response_sheet_id?: string;
  auto_import_enabled: boolean;
  import_frequency_hours: number;
}

export interface GoogleFormQuestion {
  title: string;
  type: 'multiple_choice' | 'text' | 'scale' | 'checkbox';
  required: boolean;
  options?: string[];
  scale_min?: number;
  scale_max?: number;
}

export interface GoogleFormResponse {
  id: string;
  form_id: string;
  respondent_email?: string;
  submitted_at: string;
  answers: Record<string, any>;
}

export interface SurveyMetrics {
  response_rate: number;
  average_rating: number;
  satisfaction_score: number;
  responses_by_rating: Record<number, number>;
  common_feedback_themes: string[];
}

class GoogleFormsService {
  private config: GoogleFormsConfig | null = null;
  private googleApiUrl = 'https://forms.googleapis.com/v1';
  private sheetsApiUrl = 'https://sheets.googleapis.com/v4';

  /**
   * Initialize service with configuration
   */
  async initialize(organizationId: string): Promise<void> {
    try {
      const { data: config, error } = await supabase
        .from('integration_configs')
        .select('config_data')
        .eq('organization_id', organizationId)
        .eq('integration_type', 'google_forms')
        .eq('is_active', true)
        .single();

      if (error || !config) {
        throw new Error('Google Forms configuration not found');
      }

      this.config = config.config_data as GoogleFormsConfig;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Save Google Forms configuration
   */
  async saveConfig(
    organizationId: string,
    config: GoogleFormsConfig
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('integration_configs')
        .upsert({
          organization_id: organizationId,
          integration_type: 'google_forms',
          config_data: config,
          is_active: true,
        });

      if (error) throw error;
      this.config = config;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a gift feedback survey
   */
  async createGiftFeedbackSurvey(
    giftId: string,
    recipientEmail: string,
    giftName: string
  ): Promise<{ form_id: string; form_url: string; response_url: string }> {
    try {
      // Get gift details
      const { data: gift, error: giftError } = await supabase
        .from('gifts')
        .select('*, contact:contacts(*)')
        .eq('id', giftId)
        .single();

      if (giftError) throw giftError;

      // Create form structure
      const formTitle = `Gift Feedback - ${giftName}`;
      const formDescription = `We hope you enjoyed your gift! Your feedback helps us provide better experiences in the future.`;

      const questions: GoogleFormQuestion[] = [
        {
          title: 'Overall, how satisfied were you with the gift?',
          type: 'scale',
          required: true,
          scale_min: 1,
          scale_max: 5,
        },
        {
          title: 'How well did the gift match your preferences?',
          type: 'multiple_choice',
          required: true,
          options: ['Perfect match', 'Very good', 'Good', 'Okay', 'Not my preference'],
        },
        {
          title: 'Would you recommend this type of gift to others?',
          type: 'multiple_choice',
          required: true,
          options: ['Definitely', 'Probably', 'Maybe', 'Probably not', 'Definitely not'],
        },
        {
          title: 'What did you like most about the gift?',
          type: 'text',
          required: false,
        },
        {
          title: 'What could we improve for future gifts?',
          type: 'text',
          required: false,
        },
        {
          title: 'What types of gifts would you prefer in the future?',
          type: 'checkbox',
          required: false,
          options: [
            'Experiential (spa, dining, events)',
            'Luxury goods',
            'Tech gadgets',
            'Books and media',
            'Food and beverages',
            'Home decor',
            'Personal care',
            'Other',
          ],
        },
      ];

      // In a real implementation, this would use Google Forms API
      // For now, we'll create a survey record and generate URLs
      const surveyData = {
        gift_id: giftId,
        title: formTitle,
        description: formDescription,
        questions: questions,
        recipient_email: recipientEmail,
        google_form_id: `form_${Date.now()}`,
        google_sheet_id: this.config?.response_sheet_id,
        status: 'active',
        created_at: new Date().toISOString(),
      };

      const { data: survey, error: surveyError } = await supabase
        .from('gift_surveys')
        .insert(surveyData)
        .select()
        .single();

      if (surveyError) throw surveyError;

      // Generate URLs (in production, these would come from Google API)
      const formId = survey.google_form_id;
      const formUrl = `https://docs.google.com/forms/d/${formId}/viewform`;
      const responseUrl = `https://docs.google.com/forms/d/${formId}/edit#responses`;

      // Log activity
      await supabase.from('activities').insert({
        type: 'gift_survey_created',
        title: 'Gift Feedback Survey Created',
        description: `Created feedback survey for ${giftName}`,
        related_type: 'gift',
        related_id: giftId,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      });

      return {
        form_id: formId,
        form_url: formUrl,
        response_url: responseUrl,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Import responses from Google Sheets
   */
  async importSurveyResponses(surveyId: string): Promise<number> {
    try {
      const { data: survey, error: surveyError } = await supabase
        .from('gift_surveys')
        .select('*')
        .eq('id', surveyId)
        .single();

      if (surveyError || !survey) {
        throw new Error('Survey not found');
      }

      // In a real implementation, this would fetch from Google Sheets API
      // For now, we'll simulate with sample data
      const mockResponses: GoogleFormResponse[] = [
        {
          id: `resp_${Date.now()}`,
          form_id: survey.google_form_id,
          respondent_email: survey.recipient_email,
          submitted_at: new Date().toISOString(),
          answers: {
            'Overall satisfaction': 5,
            'Match preferences': 'Perfect match',
            'Recommend': 'Definitely',
            'Liked most': 'The thoughtfulness and quality',
            'Improvements': 'Nothing, it was perfect!',
            'Future preferences': ['Experiential (spa, dining, events)', 'Luxury goods'],
          },
        },
      ];

      let importedCount = 0;

      for (const response of mockResponses) {
        // Check if response already imported
        const { data: existingFeedback } = await supabase
          .from('gift_feedback')
          .select('id')
          .eq('google_response_id', response.id)
          .single();

        if (!existingFeedback) {
          // Calculate rating from answers
          const rating = response.answers['Overall satisfaction'] || 5;
          
          // Create feedback record
          const { error: feedbackError } = await supabase
            .from('gift_feedback')
            .insert({
              gift_id: survey.gift_id,
              rating: rating,
              feedback: response.answers['Liked most'] || '',
              improvements: response.answers['Improvements'] || '',
              would_recommend: response.answers['Recommend'] === 'Definitely',
              preferences: response.answers['Future preferences'] || [],
              google_response_id: response.id,
              submitted_at: response.submitted_at,
            });

          if (!feedbackError) {
            importedCount++;
          }
        }
      }

      // Update survey last import date
      await supabase
        .from('gift_surveys')
        .update({
          last_imported_at: new Date().toISOString(),
          response_count: (survey.response_count || 0) + importedCount,
        })
        .eq('id', surveyId);

      return importedCount;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send survey invitation email
   */
  async sendSurveyInvitation(
    surveyId: string,
    customMessage?: string
  ): Promise<void> {
    try {
      const { data: survey, error } = await supabase
        .from('gift_surveys')
        .select('*, gift:gifts(*, contact:contacts(*))')
        .eq('id', surveyId)
        .single();

      if (error || !survey) {
        throw new Error('Survey not found');
      }

      const emailContent = {
        to: survey.recipient_email,
        subject: 'Your Feedback Matters - Gift Survey',
        html: `
          <h2>Thank you for your recent gift!</h2>
          <p>${customMessage || survey.description}</p>
          <p>We would greatly appreciate your feedback to help us improve our gift selection and delivery process.</p>
          <p style="margin: 20px 0;">
            <a href="${survey.google_form_id}" style="background-color: #4F46E5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">
              Take the Survey
            </a>
          </p>
          <p>The survey takes less than 2 minutes to complete.</p>
          <p>Thank you for your time!</p>
        `,
      };

      // In production, this would send via email service
      // Log the email send
      await supabase
        .from('communication_logs')
        .insert({
          type: 'email',
          recipient_type: 'contact',
          recipient_id: survey.gift.contact_id,
          subject: emailContent.subject,
          content: emailContent.html,
          status: 'sent',
          metadata: {
            survey_id: surveyId,
            gift_id: survey.gift_id,
          },
        });

      // Update survey status
      await supabase
        .from('gift_surveys')
        .update({
          invitation_sent_at: new Date().toISOString(),
          status: 'sent',
        })
        .eq('id', surveyId);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get survey metrics
   */
  async getSurveyMetrics(
    organizationId: string,
    dateRange?: { start: string; end: string }
  ): Promise<SurveyMetrics> {
    try {
      // Get all feedback for organization
      let query = supabase
        .from('gift_feedback')
        .select('*, gift:gifts!inner(organization_id)')
        .eq('gift.organization_id', organizationId);

      if (dateRange) {
        query = query
          .gte('submitted_at', dateRange.start)
          .lte('submitted_at', dateRange.end);
      }

      const { data: feedback, error } = await query;
      if (error) throw error;

      // Calculate metrics
      const totalSurveysSent = await this.getTotalSurveysSent(organizationId, dateRange);
      const responseRate = totalSurveysSent > 0 
        ? (feedback.length / totalSurveysSent) * 100 
        : 0;

      const ratings = feedback.map(f => f.rating);
      const averageRating = ratings.length > 0
        ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length
        : 0;

      const satisfactionScore = feedback.filter(f => f.rating >= 4).length / feedback.length * 100;

      const responsesByRating = ratings.reduce((acc, rating) => {
        acc[rating] = (acc[rating] || 0) + 1;
        return acc;
      }, {} as Record<number, number>);

      // Extract common themes from feedback (simplified)
      const allFeedback = feedback
        .map(f => f.feedback)
        .filter(f => f && f.length > 0)
        .join(' ');
      
      const commonThemes = this.extractCommonThemes(allFeedback);

      return {
        response_rate: responseRate,
        average_rating: averageRating,
        satisfaction_score: satisfactionScore,
        responses_by_rating: responsesByRating,
        common_feedback_themes: commonThemes,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get total surveys sent
   */
  private async getTotalSurveysSent(
    organizationId: string,
    dateRange?: { start: string; end: string }
  ): Promise<number> {
    let query = supabase
      .from('gift_surveys')
      .select('id, gift:gifts!inner(organization_id)')
      .eq('gift.organization_id', organizationId)
      .eq('status', 'sent');

    if (dateRange) {
      query = query
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end);
    }

    const { data, error } = await query;
    return data?.length || 0;
  }

  /**
   * Extract common themes from feedback text
   */
  private extractCommonThemes(text: string): string[] {
    // Simple keyword extraction (in production, use NLP)
    const keywords = [
      'quality', 'thoughtful', 'perfect', 'amazing', 'excellent',
      'improvement', 'better', 'prefer', 'suggest', 'recommend',
      'delivery', 'packaging', 'presentation', 'timing',
      'personal', 'unique', 'special', 'memorable',
    ];

    const themes: string[] = [];
    const lowerText = text.toLowerCase();

    keywords.forEach(keyword => {
      if (lowerText.includes(keyword)) {
        themes.push(keyword);
      }
    });

    return themes.slice(0, 5); // Return top 5 themes
  }

  /**
   * Create automated survey workflow
   */
  async createAutomatedWorkflow(
    organizationId: string,
    rules: {
      send_after_days: number;
      reminder_after_days?: number;
      gift_value_threshold?: number;
    }
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('automation_rules')
        .insert({
          organization_id: organizationId,
          name: 'Gift Feedback Survey Automation',
          trigger_type: 'gift_delivered',
          action_type: 'send_survey',
          conditions: {
            days_after_delivery: rules.send_after_days,
            minimum_gift_value: rules.gift_value_threshold,
          },
          actions: {
            create_survey: true,
            send_email: true,
            reminder_days: rules.reminder_after_days,
          },
          is_active: true,
        });

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Process scheduled survey tasks
   */
  async processScheduledSurveys(): Promise<void> {
    try {
      // Get all active automation rules
      const { data: rules, error: rulesError } = await supabase
        .from('automation_rules')
        .select('*')
        .eq('trigger_type', 'gift_delivered')
        .eq('action_type', 'send_survey')
        .eq('is_active', true);

      if (rulesError) throw rulesError;

      for (const rule of rules) {
        // Find gifts that match the rule criteria
        const daysAgo = new Date();
        daysAgo.setDate(daysAgo.getDate() - rule.conditions.days_after_delivery);

        const { data: gifts, error: giftsError } = await supabase
          .from('gifts')
          .select('*')
          .eq('organization_id', rule.organization_id)
          .eq('status', 'delivered')
          .gte('delivered_date', daysAgo.toISOString())
          .is('survey_sent', null);

        if (giftsError) continue;

        // Create and send surveys for eligible gifts
        for (const gift of gifts) {
          if (rule.conditions.minimum_gift_value && 
              gift.amount < rule.conditions.minimum_gift_value) {
            continue;
          }

          try {
            const survey = await this.createGiftFeedbackSurvey(
              gift.id,
              gift.recipient_email,
              gift.name
            );

            await this.sendSurveyInvitation(survey.form_id);

            // Mark gift as survey sent
            await supabase
              .from('gifts')
              .update({ survey_sent: true })
              .eq('id', gift.id);
          } catch (error) {
            // Log error but continue processing
            console.error(`Failed to send survey for gift ${gift.id}:`, error);
          }
        }
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Test Google Forms connection
   */
  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      if (!this.config) {
        throw new Error('Service not initialized');
      }

      // In production, this would test actual Google API connection
      // For now, we'll simulate a successful connection
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to connect to Google Forms',
      };
    }
  }
}

export const googleFormsService = new GoogleFormsService();
export default googleFormsService;